import React, { useMemo, useState } from 'react'

import { SquareMinus, SquarePlus } from 'lucide-react'
import { DefaultTable } from 'twyne-new.ui'

import InfoTip from 'components/InfoTip'

import { slotFormat, money, numberFormat, percent, withSmallerFormat, formatPage } from './formatters'

const ReportsProgressionTable = ({ slots }) => {
  const [expandedRows, setExpandedRows] = useState({})

  const toggleRow = rowKey => {
    setExpandedRows(state => ({
      ...state,
      [rowKey]: !state[rowKey],
    }))
  }

  const handleExpandRow = row => {
    if (row.__level === 0 && row.__hasChildren) {
      toggleRow(row.__key)
    }
  }

  const data = useMemo(() => (slots || []).reduce((acc, slot, index) => {
    const parent = {
      ...slot,
      __key: `slot-${index}`,
      __level: 0,
      __index: index,
      __hasChildren: Boolean(slot.children && slot.children.length),
    }

    acc.push(parent)

    if (expandedRows[parent.__key]) {
      acc.push({
        __key: `slot-${index}-label`,
        __level: 1,
        __index: index,
        __parentKey: parent.__key,
        __isLabelRow: true,
        value: 'Pages',
      })

      const children = (slot.children || []).map((child, childIndex) => ({
        ...child,
        __key: `slot-${index}-child-${childIndex}`,
        __level: 2,
        __index: index,
        __parentKey: parent.__key,
      }))
      acc.push(...children)
    }

    return acc
  }, []), [slots, expandedRows])

  const columns = [
    {
      isKey: true,
      dataField: 'value',
      text: 'Slot',
      formatter: (cell, row) => formatterExpandedRow({ cell, row, slots, expandedRows, toggleRow }),
    }, {
      dataField: 'total',
      text: formatterTooltip({ title: <>Total - <i><small>progression</small></i></>, tiptap: <>Total: The absolute number of impressions (including the slots skipped by internal rules).</> }),
      formatter: (cell, row) => {
        if (row.__isLabelRow) {
          return null
        }
        return row.__level === 0 ? withSmallerFormat(cell, row.__index, percent(row.totalProgressionPC, row.total)) : numberFormat(cell)
      },
    }, {
      dataField: 'completed',
      text: formatterTooltip({ title: <>Completed - <i><small>progression</small></i></>, tiptap: <>Completed: The number of impressions in which the consumer had an action (skip, continue, or a placement interaction).</> }),
      formatter: (cell, row) => {
        if (row.__isLabelRow) {
          return null
        }
        return row.__level === 0 ? withSmallerFormat(cell, row.__index, percent(row.completedProgressionPC, row.total)) : numberFormat(cell)
      },
    }, {
      dataField: 'completedPC',
      text: formatterTooltip({ title: 'Completed Percent', tiptap: <>The <b>completed</b> percentage in the current slot based on the <b>total impressions</b>.</> }),
      formatter: (cell, row) => row.__isLabelRow ? null : percent(cell, row.total),
    }, {
      dataField: 'abandonmentPC',
      text: formatterTooltip({ title: 'Abandonment Percent', tiptap: <>The <b>abandonment</b> percentage in the current slot  based on the <b>total impressions</b>.</> }),
      formatter: (cell, row) => row.__isLabelRow ? null : percent(cell, row.total),
    }, {
      dataField: 'revenue',
      text: 'Revenue',
      formatter: (cell, row) => row.__isLabelRow ? null : money(Number(cell)),
    },
  ]

  const RowWrapper = ({ children, row }) => <tr onClick={() => handleExpandRow(row)}>{children}</tr>

  return <>
    <DefaultTable
      keyField="__key"
      data={data}
      columns={columns}
      rowWrapper={RowWrapper}
      noDataIndication={() => 'No content to show.'}
    />
  </>
}

const formatterTooltip = ({ title, tiptap }) => <>
  <div className='flex items-center gap-1'>
    {title}
    <InfoTip>
      {tiptap}
    </InfoTip>
  </div>
</>

const formatterExpandedRow = ({ cell, row, slots, expandedRows, toggleRow }) => {
  if (row.__isLabelRow) {
    return <div className='text-md twy-text-base-accent pl-5'>
      Pages
    </div>
  }

  if (row.__level === 0) {
    const icon = expandedRows[row.__key] ? 'minus' : 'plus'

    return <div className='flex items-center gap-2'>
      {row.__hasChildren ? <span className='cursor-pointer hover:twy-text-base-accent' onClick={e => {
        e.stopPropagation()
        toggleRow(row.__key)
      }}>
        {icon === 'minus' ? <SquareMinus size='16' /> : <SquarePlus size='16' />}
      </span> : <span style={{ width: '14px', display: 'inline-block' }} />}
      <span>{slotFormat(cell, row.__index, slots.length)}</span>
    </div>
  }

  return <div style={{ paddingLeft: '22px' }}>
    {formatPage(cell, row)}
  </div>
}

export default ReportsProgressionTable
